import { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ChevronRight } from 'lucide-react'
import { Screen } from '../components/Screen'
import { Field, useLeaveGuard } from '../components/Form'
import { EmptyState, ErrorState, SaveFailedAlert, Skeleton } from '../components/States'
import { useEnv } from '../context'
import { useFeedbackThread, useFeedbackThreads, useInvalidateFeedback, useSingleFlight } from '../data/hooks'
import { formatDayShort } from '../data/schedule'
import { SliceApiError, type FeedbackMessage } from '../data/types'
import { useBackTo } from './AthleteScreen'

// Обратная связь как переписка (решение владельца 2026-10-06): обращение -
// тред с сообщениями пользователя и поддержки, видный здесь, откуда бы
// пользователь ни писал (приложение, боты Telegram и MAX). Ответ поддержки
// здесь виден всегда, с отметкой «Новый ответ»; уведомление приходит туда,
// откуда было последнее сообщение пользователя.

/** Предел длины сообщения - как на сервере (domain.MaxMessageRunes). */
const MAX_LEN = 3500

const STATUS_LABEL: Record<string, string> = {
  new: 'Получено',
  in_review: 'Рассматривается',
  in_dialog: 'Идёт переписка',
  resolved: 'Решено',
  closed: 'Закрыто',
}

const statusOf = (s: string) => STATUS_LABEL[s] ?? s

/** «6 октября, 12:30» по часам устройства. */
function when(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  return `${formatDayShort(d)}, ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
}

/** Откуда написано сообщение пользователя. */
function sourceOf(m: FeedbackMessage): string {
  switch (m.channel) {
    case 'telegram':
      return 'из бота Telegram'
    case 'max':
      return 'из бота MAX'
    case 'app':
      if (m.platform === 'telegram') return 'из мини-приложения в Telegram'
      if (m.platform === 'max') return 'из мини-приложения в MAX'
      return 'из приложения'
  }
  return ''
}

/** Куда сообщили об ответе поддержки (кроме самого приложения). */
function notifiedOf(m: FeedbackMessage): string | null {
  if (m.notifyStatus !== 'sent') return null
  if (m.notifyChannel === 'telegram') return 'Уведомление пришло в бота Telegram'
  if (m.notifyChannel === 'max') return 'Уведомление пришло в бота MAX'
  return null
}

const NOTIFY_RULE =
  'Ответ поддержки всегда виден здесь. Уведомление о нём приходит туда, откуда вы написали последнее сообщение: в бота Telegram или MAX. Если писали из приложения - здесь появится отметка «Новый ответ».'

function sendError(e: unknown): string {
  if (e instanceof SliceApiError && e.message === 'too_long') return `Слишком длинное сообщение: не больше ${MAX_LEN} знаков. Текст остался на экране.`
  if (e instanceof SliceApiError && e.kind === 'not_found') return 'Обращение недоступно. Текст остался на экране.'
  return 'Не отправлено, текст остался на экране. Попробуйте ещё раз.'
}

// ---------------------------------------------------------------------------
// Список обращений (/club/feedback)
// ---------------------------------------------------------------------------

export function FeedbackListScreen() {
  const navigate = useNavigate()
  const back = useBackTo('/club')
  const list = useFeedbackThreads()

  return (
    <Screen title="Обратная связь" back={back} hideHub>
      <p className="pc-muted" style={{ margin: '12px 0 0', fontSize: 14 }}>
        Ошибка, вопрос или идея - пишите команде ProCoach. Здесь вся переписка, откуда бы вы ни писали: из приложения или
        из ботов Telegram и MAX.
      </p>
      <button
        type="button"
        className="pc-btn pc-btn--primary pc-btn--block"
        style={{ marginTop: 16 }}
        onClick={() => navigate('/club/feedback/new')}
      >
        Новое обращение
      </button>

      <section className="pc-section" aria-labelledby="pc-feedback-threads">
        <h2 id="pc-feedback-threads" className="pc-section-title">
          Ваши обращения
        </h2>
        {list.isPending ? (
          <Skeleton rows={2} />
        ) : list.isError ? (
          <ErrorState title="Не удалось загрузить обращения" onRetry={() => void list.refetch()} />
        ) : list.data.length === 0 ? (
          <p className="pc-muted" style={{ margin: 0, fontSize: 14 }}>
            Обращений пока не было.
          </p>
        ) : (
          <ul className="pc-list pc-card" style={{ padding: '0 12px' }}>
            {list.data.map((t) => (
              <li key={t.id}>
                <button
                  type="button"
                  className="pc-list-row"
                  onClick={() => navigate(`/club/feedback/${t.id}`)}
                  aria-label={`${t.subject || 'Обращение'}${t.unread > 0 ? ', новый ответ' : ''}`}
                >
                  <span className="pc-list-row__main">
                    <span className="pc-list-row__title" style={{ display: 'block' }}>
                      {t.subject || 'Обращение'}
                    </span>
                    <span className="pc-list-row__meta" style={{ display: 'block' }}>
                      {statusOf(t.status)} / {when(t.lastAt)}
                    </span>
                    <span className="pc-list-row__meta" style={{ display: 'block' }}>
                      {t.lastFrom === 'support' ? 'Поддержка: ' : 'Вы: '}
                      {t.lastText}
                    </span>
                  </span>
                  {t.unread > 0 && (
                    <span className="pc-badge" aria-hidden="true">
                      Новый ответ
                    </span>
                  )}
                  <ChevronRight size={18} aria-hidden="true" className="pc-muted" />
                </button>
              </li>
            ))}
          </ul>
        )}
        <p className="pc-muted" style={{ margin: '12px 0 0', fontSize: 13 }}>
          {NOTIFY_RULE}
        </p>
      </section>
    </Screen>
  )
}

// ---------------------------------------------------------------------------
// Новое обращение (/club/feedback/new)
// ---------------------------------------------------------------------------

export function FeedbackNewScreen() {
  const { api, platform, toast } = useEnv()
  const navigate = useNavigate()
  const back = useBackTo('/club/feedback')
  const invalidate = useInvalidateFeedback()
  const flight = useSingleFlight()
  const [text, setText] = useState('')
  const [error, setError] = useState<string | null>(null)
  // Отправлено, экран уходит в переписку: второе нажатие не создаёт второе обращение
  const sent = useRef(false)
  const leave = useLeaveGuard(!!text.trim())

  const send = async () => {
    if (!text.trim() || sent.current) return
    setError(null)
    try {
      const t = await flight.run(() => api.createFeedbackThread(text, platform.kind))
      if (t === null) return
      sent.current = true
      void invalidate()
      toast('Отправлено команде ProCoach')
      navigate(`/club/feedback/${t.id}`, { replace: true })
    } catch (e) {
      setError(sendError(e))
    }
  }

  return (
    <Screen title="Новое обращение" back={() => leave.guard(back)} hideHub>
      <div className="pc-form">
        <Field label="Сообщение" hint={`${text.length} из ${MAX_LEN}`}>
          {(p) => <textarea {...p} className="pc-input" rows={6} maxLength={MAX_LEN} value={text} onChange={(e) => setText(e.target.value)} />}
        </Field>
        {error && <SaveFailedAlert>{error}</SaveFailedAlert>}
        <button
          type="button"
          className="pc-btn pc-btn--primary pc-btn--block"
          disabled={flight.busy || !text.trim()}
          onClick={() => void send()}
        >
          {flight.busy ? 'Отправляю…' : 'Отправить'}
        </button>
        <p className="pc-muted" style={{ margin: 0, fontSize: 13 }}>
          Ответ поддержки появится в этой переписке с отметкой «Новый ответ».
        </p>
      </div>
      {leave.dialog}
    </Screen>
  )
}

// ---------------------------------------------------------------------------
// Переписка (/club/feedback/:id)
// ---------------------------------------------------------------------------

export function FeedbackThreadScreen() {
  const { id = '' } = useParams()
  const { api, platform, toast } = useEnv()
  const navigate = useNavigate()
  const back = useBackTo('/club/feedback')
  const thread = useFeedbackThread(id)
  const invalidate = useInvalidateFeedback()
  const flight = useSingleFlight()
  const [text, setText] = useState('')
  const [error, setError] = useState<string | null>(null)
  const leave = useLeaveGuard(!!text.trim())
  // Ответы, которые были новыми при открытии: отметка остаётся до ухода с
  // экрана, хотя на сервере они уже прочитаны.
  const [fresh, setFresh] = useState<Set<string>>(() => new Set())
  const marking = useRef(false)

  const unread = thread.data?.messages.filter((m) => m.from === 'support' && !m.readAt) ?? []
  const unreadKey = unread.map((m) => m.id).join(',')
  useEffect(() => {
    if (!unreadKey || marking.current) return
    marking.current = true
    setFresh((prev) => new Set([...prev, ...unreadKey.split(',')]))
    api
      .markFeedbackRead(id)
      .then(() => invalidate(id))
      .catch(() => undefined) // не отметилось - отметится при следующем открытии
      .finally(() => {
        marking.current = false
      })
  }, [api, id, invalidate, unreadKey])

  // Ответ ушёл: повторное нажатие до перерисовки не отправит его второй раз
  const justSent = useRef(false)
  const send = async () => {
    if (!text.trim() || justSent.current) return
    setError(null)
    try {
      const m = await flight.run(() => api.replyFeedbackThread(id, text, platform.kind))
      if (m === null) return
      justSent.current = true
      setText('')
      void invalidate(id)
      toast('Ответ отправлен')
    } catch (e) {
      setError(sendError(e))
    }
  }

  if (thread.isPending) {
    return (
      <Screen title="Обращение" back={back} hideHub>
        <Skeleton rows={3} />
      </Screen>
    )
  }
  if (thread.isError) {
    const gone = thread.error instanceof SliceApiError && thread.error.kind === 'not_found'
    return (
      <Screen title="Обращение" back={back} hideHub>
        {gone ? (
          <EmptyState
            title="Обращение не найдено"
            text="Его нет среди ваших обращений."
            action={
              <button type="button" className="pc-btn pc-btn--primary" onClick={() => navigate('/club/feedback', { replace: true })}>
                К обращениям
              </button>
            }
          />
        ) : (
          <ErrorState title="Не удалось загрузить переписку" onRetry={() => void thread.refetch()} />
        )}
      </Screen>
    )
  }

  const t = thread.data
  return (
    <Screen title="Обращение" subtitle={t.subject} back={() => leave.guard(back)} hideHub>
      <p className="pc-muted" style={{ margin: '12px 0 0', fontSize: 14 }}>
        Статус: {statusOf(t.status)}
      </p>
      <ol className="pc-hub-log" aria-label="Переписка">
        {t.messages.map((m) => (
          <li key={m.id} className={`pc-hub-msg ${m.from === 'support' ? 'pc-hub-msg--hub' : 'pc-hub-msg--user'}`}>
            {m.from === 'support' && (
              <div className="pc-hub-who">
                Поддержка ProCoach{' '}
                {fresh.has(m.id) && (
                  <span className="pc-badge" style={{ marginLeft: 6 }}>
                    Новый ответ
                  </span>
                )}
              </div>
            )}
            <div className="pc-hub-text">{m.text}</div>
            <div className="pc-hub-meta">
              {m.from === 'user' ? `Вы, ${sourceOf(m)} - ${when(m.createdAt)}` : when(m.createdAt)}
            </div>
            {m.from === 'support' && notifiedOf(m) && <div className="pc-hub-meta">{notifiedOf(m)}</div>}
          </li>
        ))}
      </ol>

      <div className="pc-form" style={{ marginTop: 16 }}>
        <Field label="Ваш ответ" hint={`Уйдёт в эту же переписку. ${text.length} из ${MAX_LEN}`}>
          {(p) => (
            <textarea
              {...p}
              className="pc-input"
              rows={4}
              maxLength={MAX_LEN}
              value={text}
              onChange={(e) => {
                justSent.current = false
                setText(e.target.value)
              }}
            />
          )}
        </Field>
        {error && <SaveFailedAlert>{error}</SaveFailedAlert>}
        <button
          type="button"
          className="pc-btn pc-btn--primary pc-btn--block"
          disabled={flight.busy || !text.trim()}
          onClick={() => void send()}
        >
          {flight.busy ? 'Отправляю…' : 'Ответить'}
        </button>
        <p className="pc-muted" style={{ margin: 0, fontSize: 13 }}>
          {NOTIFY_RULE}
        </p>
      </div>
      {leave.dialog}
    </Screen>
  )
}
